import { FC, useState, useCallback, useEffect, useMemo } from "react";
import {
  Button,
  TableProps,
  SelectionState,
  SelectionStatus,
} from "@monolith-forensics/monolith-ui";
import { Check } from "lucide-react";

import {
  ImportDefinition,
  ImportRowValidation,
  ColumnMapping,
} from "./DataImport.types";

import { getImportableRows } from "./utils/getImportableRows";
import { resolveSelectedRowIndexes } from "./utils/resolveSelectedRowIndexes";
import FileUploadStep from "./steps/FileUploadStep";
import ColumnMappingStep from "./steps/ColumnMappingStep";
import DataPreviewStep from "./steps/DataPreviewStep";
import ImportProgressStep from "./steps/ImportProgressStep";

import { autoMapColumns } from "./utils/autoMapColumns";
import { resolveRowData } from "./utils/resolveRowData";
import { validateRows } from "./utils/validateRows";
import {
  FooterLeft,
  FooterRight,
  ModalBody,
  ModalFooter,
  StepCircle,
  StepConnector,
  StepIndicator,
  StepItem,
  StepLabel,
} from "./DataImport.styles";

export interface DataImportProps {
  definition: ImportDefinition;
  onClose?: () => void;
  onComplete?: () => void;
  previewTableProps?: TableProps;
}

const STEPS = ["Upload", "Map Columns", "Preview", "Import"];

const DefaultSelectionState: SelectionState = {
  selectedRowKeys: [],
  excludedRowKeys: [],
  selectionStatus: SelectionStatus.None,
  totalSelected: 0,
};

export const DataImport: FC<DataImportProps> = ({
  definition,
  onClose,
  onComplete,
  previewTableProps,
}) => {
  const [currentStep, setCurrentStep] = useState(0);
  const enabledFields = useMemo(
    () => definition.fields.filter((field) => field.enabled !== false),
    [definition.fields]
  );

  // Step 1 state
  const [csvColumnNames, setCsvColumnNames] = useState<string[]>([]);
  const [csvRows, setCsvRows] = useState<Record<string, string>[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);

  // Step 2 state
  const [columnMapping, setColumnMapping] = useState<ColumnMapping>({});

  // Step 3 state
  const [validatedRows, setValidatedRows] = useState<ImportRowValidation[]>([]);
  const [selectionState, setSelectionState] = useState<SelectionState>(
    DefaultSelectionState
  );
  const [isResolving, setIsResolving] = useState(false);
  const [resolveError, setResolveError] = useState<string | null>(null);

  // Step 4 state
  const [importComplete, setImportComplete] = useState(false);

  const handleFileParsed = useCallback(
    (columnNames: string[], rows: Record<string, string>[]) => {
      if (columnNames.length === 0) {
        setCsvColumnNames([]);
        setCsvRows([]);
        setFileName(null);
        return;
      }
      setCsvColumnNames(columnNames);
      setCsvRows(rows);
      setFileName(`${rows.length} rows`);

      const mapping = autoMapColumns(enabledFields, columnNames);
      setColumnMapping(mapping);
    },
    [enabledFields]
  );

  const selectedRowIndexes = useMemo(
    () => resolveSelectedRowIndexes(selectionState, validatedRows),
    [selectionState, validatedRows]
  );

  // Number of rows that will actually be sent to definition.onImport — i.e.
  // the user's selection intersected with rows that passed validation. Mirrors
  // the canonical filter in ImportProgressStep so the button label, the
  // step-2 gate, and the actual import all agree.
  const importableCount = useMemo(
    () => getImportableRows(validatedRows, selectedRowIndexes).length,
    [selectedRowIndexes, validatedRows]
  );

  const canProceed = useMemo(() => {
    switch (currentStep) {
      case 0:
        return csvRows.length > 0 && csvColumnNames.length > 0;
      case 1: {
        // Conditionally-applicable fields (those with `isApplicable`) are
        // enforced row-by-row at the preview step rather than gating the
        // column-mapping step — a user with no rows of the relevant type
        // shouldn't be forced to map a column they don't need.
        const requiredFields = enabledFields.filter(
          (f) => f.required && !f.isApplicable
        );
        return requiredFields.every((f) => !!columnMapping[f.key]);
      }
      case 2: {
        return importableCount > 0 && !isResolving;
      }
      case 3:
        return importComplete;
      default:
        return false;
    }
  }, [
    currentStep,
    csvRows,
    csvColumnNames,
    enabledFields,
    columnMapping,
    importableCount,
    isResolving,
    importComplete,
  ]);

  useEffect(() => {
    if (currentStep !== 2) return;
    if (validatedRows.length > 0) return;

    let cancelled = false;
    const run = async () => {
      setIsResolving(true);
      try {
        const resolved = await resolveRowData(
          csvRows,
          enabledFields,
          columnMapping
        );
        if (cancelled) return;

        const validated = validateRows(
          resolved,
          enabledFields,
          columnMapping,
          csvRows
        );
        setValidatedRows(validated);

        const validIndices = new Set<number>();
        validated.forEach((row) => {
          if (row.isValid) validIndices.add(row.rowIndex);
        });
        setSelectionState({
          selectedRowKeys: Array.from(validIndices).map((row) => String(row)),
          excludedRowKeys: [],
          selectionStatus:
            validIndices.size === validated.length
              ? SelectionStatus.All
              : validIndices.size === 0
              ? SelectionStatus.None
              : SelectionStatus.SomeIncluded,
          totalSelected: validIndices.size,
        });
      } catch (err) {
        if (!cancelled) {
          setResolveError(
            err instanceof Error ? err.message : "Failed to process import data"
          );
        }
      } finally {
        if (!cancelled) setIsResolving(false);
      }
    };
    run();

    return () => {
      cancelled = true;
    };
  }, [
    currentStep,
    csvRows,
    enabledFields,
    columnMapping,
    validatedRows.length,
  ]);

  const resetState = useCallback(() => {
    setCurrentStep(0);
    setCsvColumnNames([]);
    setCsvRows([]);
    setFileName(null);
    setColumnMapping({});
    setValidatedRows([]);
    setSelectionState(DefaultSelectionState);
    setImportComplete(false);
  }, []);

  const handleNext = () => {
    if (currentStep === 3) {
      resetState();
      onClose?.();
      return;
    }

    if (currentStep === 1) {
      setValidatedRows([]);
      setSelectionState(DefaultSelectionState);
    }

    setCurrentStep((prev) => Math.min(prev + 1, STEPS.length - 1));
  };

  const handleBack = () => {
    if (currentStep === 2) {
      setValidatedRows([]);
      setSelectionState(DefaultSelectionState);
      setResolveError(null);
    }
    setCurrentStep((prev) => Math.max(prev - 1, 0));
  };

  const handleClose = () => {
    resetState();
    onClose?.();
  };

  const handleImportComplete = () => {
    setImportComplete(true);
    onComplete?.();
  };

  const getNextButtonLabel = () => {
    if (currentStep === 2) return `Import ${importableCount} Records`;
    if (currentStep === 3) return "Done";
    return "Next";
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", flex: "1 1 auto" }}>
      <StepIndicator>
        {STEPS.map((label, index) => (
          <StepItem key={label}>
            <StepCircle
              $active={index === currentStep}
              $completed={index < currentStep}
            >
              {index < currentStep ? <Check size={14} /> : index + 1}
            </StepCircle>
            <StepLabel $active={index === currentStep}>{label}</StepLabel>
            {index < STEPS.length - 1 && (
              <StepConnector $completed={index < currentStep} />
            )}
          </StepItem>
        ))}
      </StepIndicator>

      <ModalBody>
        {currentStep === 0 && (
          <FileUploadStep
            definition={definition}
            onFileParsed={(cols, rows, file) => {
              handleFileParsed(cols, rows);
              if (cols.length > 0)
                setFileName(`${file?.name || "file"} (${rows.length} rows)`);
            }}
            parsedFileName={fileName}
            parsedRowCount={csvRows.length}
            parsedColumnCount={csvColumnNames.length}
          />
        )}

        {currentStep === 1 && (
          <ColumnMappingStep
            fields={enabledFields}
            csvColumnNames={csvColumnNames}
            csvRows={csvRows}
            columnMapping={columnMapping}
            onMappingChange={setColumnMapping}
          />
        )}

        {currentStep === 2 &&
          (resolveError ? (
            <div
              style={{ color: "#f44336", padding: "40px", textAlign: "center" }}
            >
              Error processing data: {resolveError}
            </div>
          ) : (
            <DataPreviewStep
              fields={enabledFields}
              validatedRows={validatedRows}
              selectionState={selectionState}
              onSelectionChange={setSelectionState}
              isResolving={isResolving}
              previewTableProps={previewTableProps}
            />
          ))}

        {currentStep === 3 && (
          <ImportProgressStep
            definition={definition}
            validatedRows={validatedRows}
            selectedRows={selectedRowIndexes}
            onComplete={handleImportComplete}
            onClose={handleClose}
          />
        )}
      </ModalBody>

      {!(currentStep === 3 && importComplete) && (
        <ModalFooter style={{ marginTop: "auto" }}>
          <FooterLeft>
            {currentStep > 0 && currentStep < 3 && (
              <Button size="xs" variant="subtle" onClick={handleBack}>
                Back
              </Button>
            )}
          </FooterLeft>
          <FooterRight>
            {!(currentStep === 3 && !importComplete) && (
              <Button size="xs" variant="subtle" onClick={handleClose}>
                Cancel
              </Button>
            )}
            <Button
              size="xs"
              color="primary"
              variant="contained"
              disabled={!canProceed}
              onClick={handleNext}
            >
              {getNextButtonLabel()}
            </Button>
          </FooterRight>
        </ModalFooter>
      )}
    </div>
  );
};
